import React, { useState, useEffect, useRef } from 'react';
import { Delivery, updateDelivery } from '../../../model/delivery';
import { Order, printShippingLabels, OutgoingItemGroup } from '../../../model/order';
import { Card } from '../../../components/Card/Card';
import { Button } from '../../../components/Button/Button';
import { TextField } from '../../../components/Form/TextField/TextField';
import { Form } from '../../../components/Form/Form';
import { SortableList } from '../../../components/SortableList/SortableList';
import { getProductName, ProductVariant } from '../../../model/product';
import { handleError, handleSuccess } from '../../../model/notification';
import classnames from 'classnames';
import { history } from '../../../model/history';
import { fulfillmentPath } from '../../../routes';
import { t } from '../../../locales/translate';
import { mergePDF, DownloadLink, downloadFiles } from '../../../util/util';
import { useData } from '../../../model/data';
import { compact } from 'lodash';
import { Stack } from '../../../components/Stack/Stack';
import { useModalPresenter } from '../../../components/Modal/PresentedModal';
import { ConfirmDeliveryModal } from './confirm_delivery';

interface Props {
  anchorId?: string;
  delivery: Delivery & { order: Order };
}

interface FulfillmentItem {
  id: string;
  sku?: string;
  validationCode?: string | null;
  productSku?: string;
  productName: string;
  variant?: Partial<ProductVariant>;
  excludedFromFulfillment: boolean;
}

// Test if the query matches the SKU
function queryMatchesItemSKU(queryToUpperCase: string, item: FulfillmentItem): boolean {
  return item.sku === queryToUpperCase;
}

// Test if the query matches the EAN
function queryMatchesItemEAN(query: string, item: FulfillmentItem): boolean {
  return item.variant?.ean === query;
}

// Test if the query includes the validation code
function queryIncludesItemValidationCode(query: string, item: FulfillmentItem): boolean {
  return !!item.validationCode && query.includes(item.validationCode);
}

// Test if the query is the legacy ID.
function queryMatchesItemLegacyId(query: string, item: FulfillmentItem): boolean {
  return item.variant?.legacyId === query;
}

// Test if the query is the legacy ID as an EAN-13 number.
function queryMatchesItemLegacyIdAsEAN(queryAsEanInt: number | false, item: FulfillmentItem): boolean {
  if (queryAsEanInt) {
    const legacyIdAsInt = Number.parseInt(item.variant?.legacyId || '');
    return !!legacyIdAsInt && legacyIdAsInt === queryAsEanInt;
  }
  return false;
}

function findItem(collection: FulfillmentItem[], query: string): FulfillmentItem | undefined {
  const queryToUpperCase = query.toUpperCase();
  const queryAsEanInt = query.length === 13 && Number.parseInt(query.slice(0, -1));
  return collection.find(
    (item) =>
      queryMatchesItemSKU(queryToUpperCase, item) ||
      queryMatchesItemEAN(query, item) ||
      queryIncludesItemValidationCode(query, item) ||
      queryMatchesItemLegacyId(query, item) ||
      queryMatchesItemLegacyIdAsEAN(queryAsEanInt, item)
  );
}

export function OrderProducts({ anchorId, delivery }: Props): JSX.Element {
  const modal = useModalPresenter();
  const inputProductSKU = useRef<HTMLInputElement | null>(null);
  // Fulfillment Items to handle
  const fulfillmentAndExcludedItems: FulfillmentItem[] = [];
  // Items in this delivery
  const itemsInThisDelivery = delivery.order.itemGroups?.filter(
    (item) => item.deliveryId === delivery.id
  ) as OutgoingItemGroup[];
  // Add items to this fulfillment
  itemsInThisDelivery?.forEach((item) => {
    item.itemIds?.forEach((id) => {
      fulfillmentAndExcludedItems.push({
        id,
        sku: item.sku,
        productSku: item.product?.sku,
        validationCode: item.tickets?.find((t) => t.id === id)?.validationCode,
        productName: getProductName(item.product),
        variant: item.productVariant,
        excludedFromFulfillment: item.invalidated,
      });
    });
  });
  const fulfillmentItems = fulfillmentAndExcludedItems.filter((i) => !i.excludedFromFulfillment);

  const ticketsInThisDelivery = compact(itemsInThisDelivery.flatMap((item) => item.tickets).map((t) => t?.url) || []);

  const [scanProductModel, setScanProductModel] = useState({ query: '' });
  const [selectedProducts, setSelectedProducts] = useState<FulfillmentItem[]>([]);
  const [success, setSuccess] = useState(false);
  // When you scan a product, you want to search in a list with the remainder items, and take it off that list in order
  // to scan the next product
  const [unscannedItems, setUnscannedItems] = useState(fulfillmentItems);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  function selectProduct(model: { query: string }): void {
    const newItem = findItem(unscannedItems, model.query);

    try {
      if (!findItem(fulfillmentItems, model.query)) {
        throw new Error(t.errors.itemNotInDelivery);
      }
      if (!newItem) {
        throw new Error(t.errors.itemAlreadyScanned);
      }

      if (selectedProducts.length === 0 && inputProductSKU && inputProductSKU.current) {
        inputProductSKU.current.scrollIntoView();
      }
      setUnscannedItems(unscannedItems.filter((item) => item.id !== newItem.id));
      setSelectedProducts([...selectedProducts, newItem]);

      setScanProductModel({ query: '' });
    } catch (error) {
      handleError(error);
    }
  }

  const [allowPrintTickets, allowPrintLabel, useNewCSVDownload] = useData((db) => [
    db.get('printTickets'),
    db.get('printLabel'),
    db.get('useNewCSVDownload'),
  ]);

  async function autoDownloadFiles({
    label,
    tickets,
    invoices,
  }: {
    label?: boolean;
    tickets?: boolean;
    invoices?: boolean;
  }): Promise<void> {
    const filesToDownload: DownloadLink[] = [];

    if (label) {
      if (!useNewCSVDownload) {
        await printShippingLabels([delivery.order], [delivery.id]);
      } else {
        filesToDownload.push({
          url: delivery.dhlLabelsUrl,
        });
      }
    }
    if (tickets) {
      filesToDownload.push({
        blob: await mergePDF(ticketsInThisDelivery),
        filename: 'tickets',
      });
    }
    if (invoices) {
      filesToDownload.push({
        blob: await mergePDF(compact(delivery.order.invoices.map((invoice) => invoice.documentUrl))),
        filename: 'invoices',
      });
    }

    downloadFiles(filesToDownload);
  }

  useEffect(() => {
    if (delivery.status !== 'sent') {
      autoDownloadFiles({
        label: allowPrintLabel && (delivery.address.countryCode === 'de' || useNewCSVDownload),
        tickets: allowPrintTickets && !!ticketsInThisDelivery.length,
        invoices: delivery.requiresInvoiceDownload,
      });
    }
  }, []);

  const [scanLabelModel, setScanLabelModel] = useState({ query: '' });
  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);

  async function submitDeliveryBooking(query: string): Promise<void> {
    const orderItem = findItem(fulfillmentItems, query);

    setLoadingUpdateStatus(true);
    try {
      if (orderItem || query === delivery.reference) {
        setScanLabelModel({ query: '' });
        if (inputRef && inputRef.current) {
          inputRef.current.focus();
        }
        throw new Error(t.errors.notTrackingCode(query));
      }
      await updateDelivery(delivery.id, {
        orderId: delivery.order.id,
        status: 'sent',
        trackingCode: query,
      });
      history.push(fulfillmentPath());
      setSuccess(true);
      handleSuccess(t.page.fulfillmentDetails.deliverySuccess, 5000);
    } catch (error) {
      setSuccess(false);
      handleError(error);
    }
    setLoadingUpdateStatus(false);
  }

  function handleOnSubmit(model: { query: string }): void {
    const query = model.query.trim();
    const queryValidationRegex = /^(\d{13}|.{1,7})$/;

    // Checks if query is < 8 characters or is valid EAN-13
    if (queryValidationRegex.test(query)) {
      modal.present(ConfirmDeliveryModal, {
        onSubmit: () => submitDeliveryBooking(query),
        deliveryCode: query,
      });
    } else {
      submitDeliveryBooking(query);
    }
  }

  const displayPrintLabelButton =
    fulfillmentItems.length > 0 && (delivery.status === 'sent' || selectedProducts.length === fulfillmentItems.length);

  const [ignoreProblem, setIgnoreProblem] = useState(false);

  if (delivery.order.status === 'problem' && !ignoreProblem) {
    return (
      <Card title={t.page.fulfillmentDetails.problematicOrderTitle}>
        <Stack justify="left">
          <p>{t.page.fulfillmentDetails.problematicOrderText}</p>

          <Button
            type="primary"
            title={t.page.fulfillmentDetails.fulfillAnyway}
            onClick={() => setIgnoreProblem(true)}
          />
        </Stack>
      </Card>
    );
  }

  return (
    <Card
      anchorId={anchorId}
      title={
        delivery.status === 'sent' ? t.page.fulfillmentDetails.packedProducts : t.page.fulfillmentDetails.productsToPack
      }
      actions={
        !!ticketsInThisDelivery.length && (
          <Button
            type="primary"
            title={t.page.fulfillmentDetails.printTicket}
            onClick={() => {
              autoDownloadFiles({ tickets: true });
            }}
          />
        )
      }
      fullbleed
    >
      {fulfillmentItems.length === 0 && (
        <footer className="pack-products__footer">{t.page.fulfillmentDetails.noProductsToFulfill}</footer>
      )}
      {fulfillmentAndExcludedItems.length > 0 && (
        <>
          {delivery.status !== 'sent' && (
            <header className="pack-products__header">
              <Form model={scanProductModel} onInput={(m) => setScanProductModel(m)} onSubmit={selectProduct}>
                <TextField
                  name="query"
                  textFieldRef={inputProductSKU}
                  autoFocus
                  displayOptional={false}
                  label={t.page.fulfillmentDetails.scanProductOrSku}
                />
              </Form>
            </header>
          )}
          <SortableList
            className="pack-products__list"
            items={fulfillmentAndExcludedItems.map((item) => {
              return {
                ...item,
                selected: selectedProducts.find((p) => p.id === item.id),
              };
            })}
            rowClass={(row) => `pack-products__product-${row.productSku}`}
            defaultSort={{ sortBy: 'sku', sortAscending: true }}
            columns={[
              {
                dataKey: 'sku',
                label: t.model.product.sku,
                cellRenderer: (row) => (
                  <div
                    className={classnames('pack-products__cell', {
                      'pack-products__cell--selected': row.selected,
                      'pack-products__cell--excluded': row.excludedFromFulfillment,
                    })}
                  >
                    <div>
                      {row.sku}
                      {row.variant?.legacyId && (
                        <div>
                          <small>{row.variant?.legacyId}</small>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                dataKey: 'validationCode',
                label: t.page.fulfillmentDetails.validationCode,
                cellRenderer: (row) => (
                  <div
                    className={classnames('pack-products__cell', {
                      'pack-products__cell--selected': row.selected,
                      'pack-products__cell--excluded': row.excludedFromFulfillment,
                    })}
                  >
                    {row.validationCode}
                  </div>
                ),
              },
              {
                dataKey: 'productName',
                label: t.page.fulfillmentDetails.productName,
                cellRenderer: (row) => (
                  <div
                    className={classnames('pack-products__cell', {
                      'pack-products__cell--selected': row.selected,
                      'pack-products__cell--excluded': row.excludedFromFulfillment,
                    })}
                  >
                    {row.productName}
                  </div>
                ),
              },
              {
                dataKey: 'variant',
                label: t.model.variant._name,
                cellRenderer: (row) => (
                  <div
                    className={classnames('pack-products__cell', {
                      'pack-products__cell--selected': row.selected,
                      'pack-products__cell--excluded': row.excludedFromFulfillment,
                    })}
                  >
                    {row.variant?.description}
                  </div>
                ),
              },
            ]}
          />
          <footer className="pack-products__footer">
            {delivery.status === 'sent' && (
              <>
                <div>{t.page.fulfillmentDetails.orderSent}</div>
                <br />
                {delivery.trackingCode && (
                  <div>
                    <strong>{t.model.delivery.trackingCode}: </strong>
                    {delivery.trackingCode}
                  </div>
                )}
              </>
            )}
            <br />
            {displayPrintLabelButton && (
              <Form model={scanLabelModel} onInput={(m) => setScanLabelModel(m)} onSubmit={handleOnSubmit}>
                <TextField
                  name="query"
                  label={t.page.fulfillmentDetails.scanLabel}
                  autoFocus={delivery.status !== 'sent'}
                  textFieldRef={inputRef}
                  required
                />
                <div className="form-actions">
                  {delivery.status !== 'sent' && (
                    <Button
                      isSubmit
                      type="primary"
                      title={t.page.fulfillmentDetails.bookDeliveryNote}
                      loading={loadingUpdateStatus}
                      success={success}
                    />
                  )}
                  <Button
                    title={t.page.fulfillmentDetails.printShippingLabel}
                    onClick={() => {
                      autoDownloadFiles({ label: true });
                    }}
                  />
                </div>
              </Form>
            )}
          </footer>
        </>
      )}
    </Card>
  );
}
